import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { litersInputValue, recordIds, tryParseLiters } from '@dairy/shared';
import { useProfile, useSettings } from '@/auth/useSession';
import { DateField } from '@/components/DateField';
import { LitersInput } from '@/components/inputs';
import { toast } from '@/components/toast';
import { Badge, Button, Card, Field, PageHeader, Textarea } from '@/components/ui';
import { errorMessage } from '@/components/errors';
import { db } from '@/db/db';
import { isLocked, save } from '@/db/repo';
import { useToday } from '@/db/hooks';

const MAX_ML = 200_000;

/** Screen 4 — Daily milk usage (MR-4): calves, home, spoiled; one record per date (deterministic id). */
export function MilkUsagePage() {
  const { t } = useTranslation();
  const profile = useProfile();
  const s = useSettings();
  const today = useToday();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const date = params.get('date') ?? today;
  const id = recordIds.dailyMilkUsage(profile.tenantId, date);
  const existing = useLiveQuery(() => db.dailyMilkUsage.get(id), [id]);
  const [calf, setCalf] = useState('');
  const [home, setHome] = useState('');
  const [spoiled, setSpoiled] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setCalf(existing ? litersInputValue(existing.calfMl) : '');
    setHome(existing ? litersInputValue(existing.homeMl) : '');
    setSpoiled(existing ? litersInputValue(existing.spoiledMl) : '');
    setNote(existing?.note ?? '');
  }, [existing, id]);

  const locked = !!existing && isLocked(existing, profile, s);

  const submit = async () => {
    const values = [calf, home, spoiled].map((v) => tryParseLiters(v));
    if (values.some((v) => !v.ok || (v.ml ?? 0) > MAX_ML)) {
      setError(t('milk.usageInvalid'));
      return;
    }
    const [c, h, sp] = values.map((v) => (v.ok ? (v.ml ?? 0) : 0)) as [number, number, number];
    try {
      await save('dailyMilkUsage', { ...(existing ?? {}), id, date, calfMl: c, homeMl: h, spoiledMl: sp, note: note.trim() || null });
      toast(navigator.onLine ? t('common.saved') : t('common.savedOffline'));
      navigate(-1);
    } catch (e) {
      setError(errorMessage(t, e));
    }
  };

  return (
    <div className="mx-auto max-w-lg">
      <PageHeader title={t('milk.usageTitle')} />
      <Card className="space-y-4">
        <DateField value={date} max={today} onChange={(d) => setParams({ date: d })} />
        {locked ? <Badge>{t('common.lockedAskOwner')}</Badge> : null}
        <Field label={t('milk.usageCalves')}>
          <LitersInput value={calf} onChange={(e) => setCalf(e.target.value)} disabled={locked} enterKeyHint="next" />
        </Field>
        <Field label={t('milk.usageHome')}>
          <LitersInput value={home} onChange={(e) => setHome(e.target.value)} disabled={locked} enterKeyHint="next" />
        </Field>
        <Field label={t('milk.usageSpoiled')}>
          <LitersInput value={spoiled} onChange={(e) => setSpoiled(e.target.value)} disabled={locked} />
        </Field>
        <Field label={t('common.note')}>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} disabled={locked} />
        </Field>
        {error ? <p className="text-red-700">{error}</p> : null}
        <Button className="w-full" size="lg" onClick={() => void submit()} disabled={locked}>
          {t('common.save')}
        </Button>
      </Card>
    </div>
  );
}
